import React, { useEffect, useState } from "react";
import {
    Alert,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

import { sendPaymentReceiptEmail } from "../../../api/requests/payment-requests";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * PaymentReceiptEmailModal – Ödeme makbuzunu e-posta ile gönderir.
 *
 * İki seçenek sunar: ödemeyi yapanın kayıtlı adresi ya da elle girilen bir adres.
 * PDF sunucuda üretilir; burada yalnızca hedef adres belirlenir.
 */
const PaymentReceiptEmailModal = ({ isOpen, toggle, payment }) => {
    const ownerEmail = payment?.emailSnapshot || "";

    const [mode, setMode] = useState("owner");
    const [customEmail, setCustomEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setMode(ownerEmail ? "owner" : "custom");
            setCustomEmail("");
            setError(null);
        }
    }, [isOpen, ownerEmail]);

    if (!payment) return null;

    const handleSend = async () => {
        const target = mode === "owner" ? ownerEmail : customEmail.trim();

        if (!target) {
            setError("Lütfen makbuzun gönderileceği e-posta adresini girin.");
            return;
        }
        if (!EMAIL_PATTERN.test(target)) {
            setError("Geçerli bir e-posta adresi girin.");
            return;
        }

        setSending(true);
        setError(null);
        try {
            // Kayıtlı adrese gönderimde body boş gider; sunucu emailSnapshot'ı kullanır
            const json = await sendPaymentReceiptEmail(payment.id, mode === "owner" ? null : target);
            toast.success(json.message || `Makbuz ${target} adresine gönderildi.`);
            toggle();
        } catch (e) {
            setError(e.message || "Makbuz gönderilemedi.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>
                <i className="bx bx-envelope me-2" />
                Makbuzu E-posta ile Gönder
            </ModalHeader>

            <ModalBody>
                <p className="text-muted small mb-3">
                    <strong>{payment.merchantOid}</strong> numaralı işlemin makbuzu PDF olarak gönderilecek.
                </p>

                {error && (
                    <Alert color="danger" className="rounded-4 py-2 small">
                        <i className="bx bx-error-circle me-1" />
                        {error}
                    </Alert>
                )}

                <div className="d-flex flex-column gap-2">
                    <label className={`d-flex align-items-start gap-2 p-3 rounded-4 border ${mode === "owner" ? "border-primary" : ""}`}
                           style={{ cursor: ownerEmail ? "pointer" : "not-allowed", opacity: ownerEmail ? 1 : 0.5 }}>
                        <Input
                            type="radio"
                            name="receiptEmailMode"
                            className="mt-1"
                            checked={mode === "owner"}
                            disabled={!ownerEmail}
                            onChange={() => setMode("owner")}
                        />
                        <span>
                            <span className="fw-semibold d-block">İşlemi yapana gönder</span>
                            <span className="text-muted small">
                                {ownerEmail || "Bu ödemede kayıtlı e-posta adresi yok"}
                            </span>
                        </span>
                    </label>

                    <label className={`d-flex align-items-start gap-2 p-3 rounded-4 border ${mode === "custom" ? "border-primary" : ""}`}
                           style={{ cursor: "pointer" }}>
                        <Input
                            type="radio"
                            name="receiptEmailMode"
                            className="mt-1"
                            checked={mode === "custom"}
                            onChange={() => setMode("custom")}
                        />
                        <span className="flex-grow-1">
                            <span className="fw-semibold d-block">Başka bir adrese gönder</span>
                            <Input
                                type="email"
                                className="mt-2"
                                placeholder="ornek@firma.com"
                                value={customEmail}
                                onFocus={() => setMode("custom")}
                                onChange={(e) => setCustomEmail(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
                            />
                        </span>
                    </label>
                </div>
            </ModalBody>

            <ModalFooter className="border-0 pt-0">
                <Button color="secondary" outline className="btn-rounded" onClick={toggle} disabled={sending}>
                    İptal
                </Button>
                <Button color="primary" className="btn-rounded" onClick={handleSend} disabled={sending}>
                    {sending ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-send me-1" />}
                    Gönder
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default PaymentReceiptEmailModal;
